import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Quote, Sparkles } from "lucide-react";

const TESTIMONIALS = [
  {
    role: "Guest",
    name: "Aarav Mehta",
    city: "Bangalore",
    quote: "Paid for my PG with the money I saved on lunch. Genuinely — Value Pool pays for itself in two outings.",
  },
  {
    role: "Business Partner",
    name: "The Cozy Cafe",
    city: "Koramangala",
    quote: "Verified guests = zero coupon-haggling. Foot-traffic jumped 22% in the first month after we joined Value Pool.",
  },
  {
    role: "Sales Partner",
    name: "Nisha R.",
    city: "Pune",
    quote: "Signing up hostels in my city earns recurring rewards I can actually see in my dashboard. Best side income I've had.",
  },
  {
    role: "Guest",
    name: "Rafael Costa",
    city: "Goa",
    quote: "The Verified badge got me a hostel booking at peak season in under an hour. Worth the membership ten times over.",
  },
];

function Testimonial({ filter }) {
  const items = filter
    ? TESTIMONIALS.filter((t) => t.role === filter).concat(TESTIMONIALS)
    : TESTIMONIALS;
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((p) => (p + 1) % items.length), 5500);
    return () => clearInterval(id);
  }, [items.length]);
  const t = items[idx];
  return (
    <div className="relative" data-testid="auth-testimonial">
      <div className="flex items-center gap-2 mb-3">
        <Quote className="w-4 h-4 text-secondary" />
        <span className="text-[11px] uppercase tracking-wider text-secondary font-semibold">{t.role}</span>
        <div className="flex items-center gap-0.5 text-secondary ml-auto">
          {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-current" />)}
        </div>
      </div>
      <blockquote className="font-heading text-lg md:text-xl leading-snug">
        “{t.quote}”
      </blockquote>
      <div className="mt-4 text-sm opacity-90">
        <b>{t.name}</b> · {t.city}
      </div>
      <div className="flex gap-1.5 mt-5">
        {items.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIdx(i)}
            aria-label={`Go to testimonial ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-secondary" : "w-1.5 bg-foreground/15 hover:bg-foreground/30"}`}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Half-page auth shell.
 *
 *  ┌─────────────────────────┬─────────────────────────┐
 *  │  cream brand panel      │  white form column      │
 *  │  · logo big             │  · auth card            │
 *  │  · heading              │  · footer link          │
 *  │  · sub-headline         │                         │
 *  │  · rotating testimonial │                         │
 *  └─────────────────────────┴─────────────────────────┘
 *
 *  On mobile the brand panel collapses to a thin top strip with just
 *  the logo + name + tagline so the form is always above the fold.
 */
export default function AuthShell({ heading, subhead, footerTag, testimonialFilter, children }) {
  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      {/* Brand / story panel */}
      <aside className="relative overflow-hidden px-5 md:px-10 lg:px-14 py-6 md:py-12 flex flex-col bg-background">
        {/* Decorative wash — uses brand teal + orange */}
        <div className="absolute inset-0 pointer-events-none hidden md:block"
             style={{
               background:
                 "radial-gradient(at 15% 10%, rgba(0,169,145,0.14), transparent 55%)," +
                 "radial-gradient(at 90% 90%, rgba(248,154,61,0.16), transparent 55%)",
             }} />
        {/* Watermark logo bottom-right */}
        <img src="/valuepool-logo.png" alt=""
             aria-hidden="true"
             className="absolute -right-20 -bottom-24 w-[440px] h-[440px] object-contain opacity-[0.06] pointer-events-none select-none hidden md:block" />

        <Link to="/" className="relative flex items-center gap-3 w-fit" data-testid="auth-brand-home">
          <img src="/valuepool-logo.png" alt="Value Pool" className="h-12 md:h-16 w-auto shrink-0" />
          <div className="leading-tight">
            <div className="font-heading text-xl md:text-2xl font-extrabold text-primary">Value Pool</div>
            <div className="text-[10px] md:text-[11px] uppercase tracking-[0.14em] font-semibold text-secondary mt-1">Pool of Smart Savings</div>
          </div>
        </Link>

        <div className="relative hidden md:flex flex-col flex-1 justify-center max-w-md mt-6">
          {heading && (
            <h2 className="font-heading text-3xl lg:text-5xl font-extrabold tracking-tight leading-[1.02] text-foreground uppercase">
              {heading}
            </h2>
          )}
          {subhead && (
            <p className="mt-4 body-text text-base lg:text-lg">{subhead}</p>
          )}

          <div className="mt-10 pt-8 border-t border-border/60">
            <Testimonial filter={testimonialFilter} />
          </div>
        </div>

        {footerTag && (
          <div className="relative hidden md:flex items-center gap-2 mt-auto pt-6 text-xs font-mono text-muted-foreground/70">
            <Sparkles className="w-3 h-3" /> {footerTag}
          </div>
        )}
      </aside>

      {/* Form column */}
      <main className="flex items-center justify-center px-5 md:px-10 lg:px-14 py-6 md:py-12 bg-card md:border-l md:border-border">
        <div className="w-full max-w-md">
          {/* mobile-only condensed heading */}
          {heading && (
            <div className="md:hidden mb-5">
              <h1 className="font-heading text-2xl font-extrabold tracking-tight uppercase">{heading}</h1>
              {subhead && <p className="text-sm body-text mt-1.5">{subhead}</p>}
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
